import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, Globe, User, Phone } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import Button from '../components/common/Button';
import { ReactComponent as RodBotLogo } from '../assets/images/rodbotsvg.svg';
import './LoginPage.css';
import './RegisterPage.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Normalize an Israeli / international phone into +<digits>. Returns null if implausible.
const normalizePhone = (input) => {
  if (!input) return null;
  const hasPlus = String(input).trim().startsWith('+');
  let digits = String(input).replace(/\D/g, '');
  if (!digits) return null;
  if (!hasPlus) {
    if (digits.startsWith('0')) digits = '972' + digits.slice(1);
    else if (digits.length === 9) digits = '972' + digits;
  }
  // plausible international length: 10–15 digits
  if (digits.length < 10 || digits.length > 15) return null;
  return '+' + digits;
};

const scorePassword = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
};

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const { currentLanguage, changeLanguage, availableLanguages, isRTL } = useLanguage();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const pwStrength = useMemo(() => scorePassword(password), [password]);
  const strengthLabel = [
    t('auth.passwordStrengthWeak', 'Weak'),
    t('auth.passwordStrengthWeak', 'Weak'),
    t('auth.passwordStrengthMedium', 'Medium'),
    t('auth.passwordStrengthMedium', 'Medium'),
    t('auth.passwordStrengthStrong', 'Strong'),
  ][pwStrength];

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = t('auth.nameRequired', 'Please enter your name');
    if (!EMAIL_RE.test(email.trim())) next.email = t('auth.invalidEmail', 'Please enter a valid email address');
    if (!normalizePhone(phone)) next.phone = t('auth.invalidPhone', 'Please enter a valid phone number');
    if (password.length < 8) {
      next.password = t('auth.passwordTooShort', 'Password must be at least 8 characters');
    } else if (!(/[A-Za-z]/.test(password) && /\d/.test(password))) {
      next.password = t('auth.passwordNeedsLetterNumber', 'Password must contain both letters and numbers');
    }
    if (confirmPassword !== password) {
      next.confirmPassword = t('auth.passwordsDontMatch', 'Passwords do not match');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: normalizePhone(phone),
        password,
      });

      if (result.success) {
        toast.success(t('auth.registerSuccess', 'Account created! Welcome aboard.'));
        navigate('/');
      } else {
        toast.error(result.error || t('auth.registerError', 'Registration failed'));
      }
    } catch (error) {
      toast.error(t('auth.registerError', 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const currentLang = availableLanguages.find((l) => l.code === currentLanguage);

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className={`login-page ${isRTL ? 'rtl' : ''}`}>
      {/* Language Selector */}
      <div className="login-lang-selector">
        <button className="lang-button" onClick={() => setShowLangMenu(!showLangMenu)}>
          <Globe size={18} />
          <span>{currentLang?.nativeName}</span>
        </button>

        {showLangMenu && (
          <div className="lang-menu">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                className={`lang-item ${lang.code === currentLanguage ? 'active' : ''}`}
                onClick={() => {
                  changeLanguage(lang.code);
                  setShowLangMenu(false);
                }}
              >
                {lang.nativeName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Background decoration */}
      <div className="login-bg">
        <div className="login-bg-gradient"></div>
        <div className="login-bg-pattern"></div>
      </div>

      {/* Register Card */}
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <RodBotLogo className="login-logo-svg" />
            </div>
            <h1 className="login-title">{t('auth.createAccount', 'Create account')}</h1>
            <p className="login-subtitle">
              {t('auth.registerSubtitle', 'Start managing your WhatsApp leads in minutes')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">{t('auth.name', 'Full name')}</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.name', 'Full name')}
                  className="form-input"
                  autoComplete="name"
                />
              </div>
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">{t('auth.email', 'Email')}</label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email', 'Email')}
                  className="form-input"
                  autoComplete="username"
                />
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            {/* Phone */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">{t('auth.phone', 'Phone number')}</label>
              <div className="input-wrapper">
                <Phone size={18} className="input-icon" />
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+972 5X-XXX-XXXX"
                  className="form-input"
                  autoComplete="tel"
                  dir="ltr"
                />
              </div>
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">{t('auth.password', 'Password')}</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password', 'Password')}
                  className="form-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (
                <div className="pw-strength">
                  <div className={`pw-strength-bar level-${pwStrength}`}>
                    <span></span><span></span><span></span><span></span>
                  </div>
                  <span className="pw-strength-label">{strengthLabel}</span>
                </div>
              )}
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                {t('auth.confirmPassword', 'Confirm password')}
              </label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmPassword', 'Confirm password')}
                  className="form-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="login-button"
            >
              {loading ? t('auth.registeringIn', 'Creating account...') : t('auth.registerButton', 'Create account')}
            </Button>
          </form>

          <p className="auth-switch">
            {t('auth.haveAccount', 'Already have an account?')}{' '}
            <Link to="/login" className="auth-switch-link">{t('auth.login', 'Login')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
